import { ESourceTypes } from '@common/definitions';

export interface PushNotificationPayload {
  type?: string;
  source_type?: string;
  source_id?: string | number;
  id?: string | number;
  conversation_id?: string | number;
  track_name?: string;
  activity_type?: string;
  url?: string;
  deeplink?: string;
  path?: string;
  route?: string;
  target_path?: string;
  meta?: {
    conversation?: {
      id?: string;
    };
    channel?: {
      id?: string;
    };
  };
}

const RESERVED_CONVERSATION_SEGMENTS = new Set(['filter', 'users']);

export function normalizeConversationId(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  return String(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * OneSignal payloads vary by channel; mirror parseAdditionalData in OneSignalSDKWorker.js.
 */
export function normalizePushNotificationData(raw: unknown): PushNotificationPayload | null {
  if (raw == null) return null;

  let data: unknown = raw;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }

  if (!isRecord(data)) return null;

  const nested =
    data.additionalData ??
    data.additional_data ??
    data.additional_data_json ??
    (isRecord(data.custom) ? data.custom.a : undefined) ??
    data.custom_a ??
    data.a;

  if (nested != null && nested !== data) {
    return normalizePushNotificationData(nested);
  }

  return data as PushNotificationPayload;
}

export function extractPushNotificationDataFromEvent(event: unknown): PushNotificationPayload | null {
  if (!isRecord(event)) return null;

  const notification = isRecord(event.notification) ? event.notification : null;
  const candidates = [
    notification?.additionalData,
    notification?.data,
    event.additionalData,
    event.data,
  ];

  for (const candidate of candidates) {
    const normalized = normalizePushNotificationData(candidate);
    if (normalized) return normalized;
  }

  return null;
}

function conversationIdFromPathString(pathOrUrl: string): string | undefined {
  try {
    const path = pathOrUrl.startsWith('/')
      ? pathOrUrl
      : new URL(pathOrUrl, 'https://placeholder.local').pathname;
    return getActiveConversationIdFromLocation(path, '');
  } catch {
    return getActiveConversationIdFromLocation(pathOrUrl, '');
  }
}

/**
 * Conversation id from URL when the user is on a chat thread.
 * Supports direct, filter, list (?conversation=), and /admin/conversations routes.
 */
function isChatRoutePath(pathname: string): boolean {
  return /^\/(?:admin\/)?conversations(?:\/|$)/.test(pathname);
}

export function getActiveConversationIdFromLocation(
  pathname: string,
  search: string = ''
): string | undefined {
  if (!isChatRoutePath(pathname)) return undefined;

  const fromQuery = new URLSearchParams(search).get('conversation');
  if (fromQuery) return fromQuery;

  const filterWithId = pathname.match(
    /\/(?:admin\/)?conversations\/filter\/([^/]+)\/([^/]+)/
  );
  if (filterWithId) {
    const [, filterName, segment] = filterWithId;
    if (filterName !== 'list' && segment && !RESERVED_CONVERSATION_SEGMENTS.has(segment)) {
      return segment;
    }
  }

  const direct = pathname.match(/\/(?:admin\/)?conversations\/([^/]+)$/);
  if (direct) {
    const id = direct[1];
    if (!RESERVED_CONVERSATION_SEGMENTS.has(id)) return id;
  }

  return undefined;
}

/** @deprecated Use getActiveConversationIdFromLocation */
export function getActiveConversationIdFromPath(pathname: string): string | undefined {
  return getActiveConversationIdFromLocation(pathname);
}

function getConversationIdFromMeta(
  meta: PushNotificationPayload['meta']
): string | undefined {
  return normalizeConversationId(meta?.conversation?.id);
}

/** Resolve conversation id from OneSignal `additionalData` (chat pushes). */
export function getConversationIdFromPushPayload(
  data: PushNotificationPayload | null | undefined
): string | undefined {
  if (!data) return undefined;

  const fromConversationId = normalizeConversationId(data.conversation_id);
  if (fromConversationId) return fromConversationId;

  const fromMeta = getConversationIdFromMeta(data.meta);
  if (fromMeta) return fromMeta;

  const type = data.type || data.source_type;
  if (type === ESourceTypes.CONVERSATION) {
    return normalizeConversationId(data.source_id ?? data.id);
  }

  const pathCandidates = [
    data.deeplink,
    data.path,
    data.route,
    data.target_path,
    data.url,
  ];

  for (const candidate of pathCandidates) {
    if (typeof candidate !== 'string' || !candidate) continue;
    const fromPath = conversationIdFromPathString(candidate);
    if (fromPath) return fromPath;
  }

  return undefined;
}

/** Only the current URL counts — avoids stale localStorage/chat state on other pages. */
function resolveActiveConversationId(pathname: string, search: string): string | undefined {
  return normalizeConversationId(
    getActiveConversationIdFromLocation(pathname, search)
  );
}

export function isViewingChatConversationRoute(
  pathname: string,
  search: string = ''
): boolean {
  return !!getActiveConversationIdFromLocation(pathname, search);
}

export function shouldSuppressChatPushNotification(
  data: PushNotificationPayload | null | undefined,
  pathname: string,
  search: string = ''
): boolean {
  const activeConversationId = resolveActiveConversationId(pathname, search);
  if (!activeConversationId) return false;

  const normalized = normalizePushNotificationData(data);
  const pushConversationId = getConversationIdFromPushPayload(normalized);
  if (!pushConversationId) return false;

  return pushConversationId === activeConversationId;
}

function getConversationIdFromNotificationRecord(
  record: Record<string, unknown>
): string | undefined {
  const fromPayload = getConversationIdFromPushPayload({
    type: typeof record.type === 'string' ? record.type : undefined,
    source_type: typeof record.source_type === 'string' ? record.source_type : undefined,
    source_id: record.source_id as string | number | undefined,
    id: record.id as string | number | undefined,
    conversation_id: record.conversation_id as string | number | undefined,
    url: typeof record.url === 'string' ? record.url : undefined,
    deeplink: typeof record.deeplink === 'string' ? record.deeplink : undefined,
    path: typeof record.path === 'string' ? record.path : undefined,
  });
  if (fromPayload) return fromPayload;

  const urlFields = [
    record.url,
    record.u,
    record.launchURL,
    record.launchUrl,
    record.web_url,
    record.targetUrl,
  ];

  for (const candidate of urlFields) {
    if (typeof candidate !== 'string' || !candidate) continue;
    const fromUrl = conversationIdFromPathString(candidate);
    if (fromUrl) return fromUrl;
  }

  for (const value of Object.values(record)) {
    if (typeof value !== 'string' || !value.includes('/conversations/')) continue;
    const fromEmbedded = conversationIdFromPathString(value);
    if (fromEmbedded) return fromEmbedded;
  }

  return undefined;
}

function getConversationIdFromShownNotification(
  notification: Notification
): string | undefined {
  const { data } = notification;
  if (data && typeof data === 'object') {
    const normalized = normalizePushNotificationData(data);
    const fromNormalized = getConversationIdFromPushPayload(normalized);
    if (fromNormalized) return fromNormalized;

    const fromRecord = getConversationIdFromNotificationRecord(
      data as Record<string, unknown>
    );
    if (fromRecord) return fromRecord;
  }

  if (notification.tag) {
    const fromTag = conversationIdFromPathString(notification.tag);
    if (fromTag) return fromTag;
  }

  return undefined;
}

function isLikelyChatNotification(notification: Notification): boolean {
  if (typeof notification.tag === 'string' && notification.tag.includes('/conversations/')) {
    return true;
  }

  const { data } = notification;
  if (!data || typeof data !== 'object') return false;

  const normalized = normalizePushNotificationData(data);
  const type = normalized?.type || normalized?.source_type;
  const trackName = normalized?.track_name || normalized?.activity_type;
  if (type === ESourceTypes.CONVERSATION || trackName === 'private_message') {
    return true;
  }

  const record = data as Record<string, unknown>;
  const urlFields = [
    record.url,
    record.u,
    record.launchURL,
    record.launchUrl,
    record.web_url,
    record.targetUrl,
  ];

  return urlFields.some(
    (value) => typeof value === 'string' && value.includes('/conversations/')
  );
}

/** True when no OS banner is clearly for a different chat (safe to clear unparsed). */
function canCloseUnparsedNotificationsOnOpen(
  notifications: Notification[],
  targetId: string
): boolean {
  return !notifications.some((notification) => {
    const notifConversationId = getConversationIdFromShownNotification(notification);
    return !!notifConversationId && notifConversationId !== targetId;
  });
}

interface DismissBrowserNotificationsOptions {
  /**
   * When opening a chat: also close banners with no conversation id in payload,
   * but only if no banner is clearly for another chat (keeps 16670 when opening 16577).
   */
  closeUnparsedOnOpen?: boolean;
}

/** Dismiss browser/OS notifications for a specific conversation only. */
export async function dismissBrowserNotificationsForConversation(
  conversationId: string,
  options: DismissBrowserNotificationsOptions = {}
): Promise<void> {
  const targetId = normalizeConversationId(conversationId);
  if (!targetId) return;

  if (typeof navigator === 'undefined' || !navigator.serviceWorker?.getRegistration) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration?.getNotifications) return;

    const notifications = await registration.getNotifications();
    const mayCloseUnparsed =
      options.closeUnparsedOnOpen &&
      canCloseUnparsedNotificationsOnOpen(notifications, targetId);

    notifications.forEach((notification) => {
      const notifConversationId = getConversationIdFromShownNotification(notification);

      if (notifConversationId === targetId) {
        notification.close();
        return;
      }

      if (mayCloseUnparsed && !notifConversationId && isLikelyChatNotification(notification)) {
        notification.close();
      }
    });
  } catch {
    // Non-fatal: clearing OS notifications is best-effort.
  }
}

const OPEN_CHAT_DISMISS_OPTIONS: DismissBrowserNotificationsOptions = {
  closeUnparsedOnOpen: true,
};

/** Run dismiss a few times so late-displayed OS banners are still cleared after open. */
export function dismissBrowserNotificationsWhenOpeningChat(
  conversationId: string
): void {
  if (typeof window === 'undefined') return;

  void dismissBrowserNotificationsForConversation(
    conversationId,
    OPEN_CHAT_DISMISS_OPTIONS
  );
  window.setTimeout(() => {
    void dismissBrowserNotificationsForConversation(
      conversationId,
      OPEN_CHAT_DISMISS_OPTIONS
    );
  }, 200);
  window.setTimeout(() => {
    void dismissBrowserNotificationsForConversation(
      conversationId,
      OPEN_CHAT_DISMISS_OPTIONS
    );
  }, 800);
}
